import { PaymentMethod, PaymentMethodDto, Restaurant, OFFLINE_PAYMENT_METHODS } from '../types';

export interface CheckoutPaymentMethodOption {
  id: PaymentMethod;
  name: string;
  icon: string;
  description?: string;
}

const CHECKOUT_PAYMENT_METHOD_ORDER: Record<string, number> = {
  [PaymentMethod.CASH]: 1,
  [PaymentMethod.PIX_OFFLINE]: 2,
  [PaymentMethod.PIX_ONLINE]: 3,
  [PaymentMethod.CREDIT_CARD_OFFLINE]: 4,
  [PaymentMethod.CREDIT_CARD_ONLINE]: 5,
  [PaymentMethod.DEBIT_CARD_OFFLINE]: 6,
  [PaymentMethod.DEBIT_CARD_ONLINE]: 7,
  [PaymentMethod.MEAL_VOUCHER_OFFLINE]: 8,
  [PaymentMethod.MEAL_VOUCHER_ONLINE]: 9,
  [PaymentMethod.BANK_TRANSFER]: 10,
  [PaymentMethod.STORE_CREDIT]: 11,
  [PaymentMethod.GOOGLE_PAY]: 12,
  [PaymentMethod.APPLE_PAY]: 13,
  [PaymentMethod.PAYPAL]: 14,
  [PaymentMethod.CRYPTOCURRENCY]: 15,
  [PaymentMethod.COUPON]: 16,
};

const resolveCheckoutPaymentMethodIcon = (methodId: string): string => {
  const normalizedMethodId = methodId.toUpperCase();

  switch (normalizedMethodId) {
    case PaymentMethod.CASH:
      return 'fa-money-bill-wave';
    case PaymentMethod.PIX_OFFLINE:
    case PaymentMethod.PIX_ONLINE:
    case 'PIX':
      return 'fa-qrcode';
    case PaymentMethod.MEAL_VOUCHER_ONLINE:
    case PaymentMethod.MEAL_VOUCHER_OFFLINE:
      return 'fa-utensils';
    case PaymentMethod.GOOGLE_PAY:
      return 'fa-brands fa-google-pay';
    case PaymentMethod.APPLE_PAY:
      return 'fa-brands fa-apple-pay';
    default:
      return 'fa-credit-card';
  }
};

export const getCheckoutPaymentMethodLabel = (methodId?: string | null): string => {
  if (!methodId) {
    return '';
  }

  switch (methodId.toUpperCase()) {
    case PaymentMethod.CASH:
      return 'Dinheiro';
    case PaymentMethod.CREDIT_CARD_ONLINE:
    case PaymentMethod.CREDIT_CARD_OFFLINE:
      return 'Cartão de crédito';
    case PaymentMethod.DEBIT_CARD_ONLINE:
    case PaymentMethod.DEBIT_CARD_OFFLINE:
      return 'Cartão de débito';
    case PaymentMethod.PIX_OFFLINE:
      return 'Pix';
    case PaymentMethod.PIX_ONLINE:
      return 'Pix online  (Processado pela Léo Delivery)';
    case 'PIX':
      return 'Pix';
    case PaymentMethod.MEAL_VOUCHER_ONLINE:
    case PaymentMethod.MEAL_VOUCHER_OFFLINE:
      return 'Vale refeição';
    case PaymentMethod.BANK_TRANSFER:
      return 'Transferência bancária';
    case PaymentMethod.STORE_CREDIT:
      return 'Crédito na loja';
    case PaymentMethod.CRYPTOCURRENCY:
      return 'Criptomoeda';
    case PaymentMethod.GOOGLE_PAY:
      return 'Google Pay';
    case PaymentMethod.APPLE_PAY:
      return 'Apple Pay';
    case PaymentMethod.PAYPAL:
      return 'PayPal';
    case PaymentMethod.COUPON:
      return 'Cupom';
    default:
      return methodId;
  }
};

const mapPaymentMethodToOption = (method: PaymentMethodDto): CheckoutPaymentMethodOption => ({
  id: method.id as PaymentMethod,
  name: getCheckoutPaymentMethodLabel(method.id),
  icon: resolveCheckoutPaymentMethodIcon(method.id),
});

const sortCheckoutPaymentMethods = (methods: CheckoutPaymentMethodOption[]): CheckoutPaymentMethodOption[] =>
  [...methods].sort((left, right) => {
    const leftOrder = CHECKOUT_PAYMENT_METHOD_ORDER[left.id] ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = CHECKOUT_PAYMENT_METHOD_ORDER[right.id] ?? Number.MAX_SAFE_INTEGER;

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return left.name.localeCompare(right.name);
  });

export const getCheckoutPaymentMethods = (restaurant?: Pick<Restaurant, 'paymentMethods'> | null): CheckoutPaymentMethodOption[] => {
  const acceptedMethods = restaurant?.paymentMethods?.filter(method => method.accepted) ?? [];

  if (acceptedMethods.length > 0) {
    return sortCheckoutPaymentMethods(acceptedMethods.map(mapPaymentMethodToOption));
  }

  return sortCheckoutPaymentMethods(OFFLINE_PAYMENT_METHODS.map(method => ({
    ...method,
    name: getCheckoutPaymentMethodLabel(method.id),
  })));
};

export const getCheckoutPaymentMethodIcon = (methodId?: string | null): string => {
  if (!methodId) {
    return 'fa-credit-card';
  }

  return resolveCheckoutPaymentMethodIcon(methodId);
};

export const getCheckoutPaymentMethodImageSrc = (methodId?: string | null): string | null => {
  if (!methodId) {
    return null;
  }

  const normalizedMethodId = methodId.toUpperCase();

  return normalizedMethodId === PaymentMethod.PIX_OFFLINE || normalizedMethodId === PaymentMethod.PIX_ONLINE || normalizedMethodId === 'PIX'
    ? '/pix-logo.svg'
    : null;
};
